// Matches CJK unified ideographs, compatibility ideographs, and CJK compatibility forms
const CJK_RANGE = new RegExp(
	'[\\u2E80-\\u9FFF\\uF900-\\uFAFF\\uFE30-\\uFE4F\\u{20000}-\\u{2FA1F}]',
	'u',
);

// eslint-disable-next-line compat/compat
const wordSegmenter =
	'Segmenter' in Intl ? new Intl.Segmenter(undefined, { granularity: 'word' }) : null;

const tokenizeWords = (text: string): string[] => {
	if (wordSegmenter) {
		return [...wordSegmenter.segment(text)]
			.filter((segment) => segment.isWordLike)
			.map((segment) => segment.segment);
	}
	return text.split(/\s+/).filter(Boolean);
};

const joinWords = (words: string[]): string => {
	if (words.length === 0) {
		return '';
	}
	let result = words[0];
	for (let i = 1; i < words.length; i++) {
		const needsSpace = !CJK_RANGE.test(words[i - 1]) || !CJK_RANGE.test(words[i]);
		result += needsSpace ? ' ' + words[i] : words[i];
	}
	return result;
};

// Strips everything except Unicode letters and digits for fuzzy comparison
const NORMALIZE_RE = new RegExp('[^\\p{L}\\p{N}]', 'gu');
const normalize = (word: string) => word.toLowerCase().replace(NORMALIZE_RE, '');

/**
 * Merge WebSpeech (instant, low quality) with STT LLM (slower, high quality).
 * Confirmed (STT LLM) is always the source of truth. Ghost (WebSpeech) is a
 * disposable preview — it can only contribute words beyond what confirmed covers.
 */
export const mergeTranscriptions = (
	confirmed: string,
	ghost: string,
): { text: string; isFullyResolved: boolean } => {
	if (!ghost) {
		return { text: confirmed, isFullyResolved: true };
	}
	if (!confirmed) {
		return { text: ghost, isFullyResolved: false };
	}

	const confirmedTokens = tokenizeWords(confirmed);
	const ghostTokens = tokenizeWords(ghost);

	if (confirmedTokens.length === 0) {
		return { text: ghost, isFullyResolved: false };
	}

	// Ghost can only contribute if it has more tokens than confirmed.
	// If ghost is shorter or equal, confirmed already covers everything then
	// any ghost content is stale or a re-transcription from a restarted session.
	if (ghostTokens.length <= confirmedTokens.length) {
		return { text: confirmed, isFullyResolved: true };
	}

	// Walk forward through both arrays matching normalized tokens to find
	// where confirmed content ends within ghost, handling punctuation and
	// casing differences (e.g. ghost "me" vs confirmed "me.").
	let ghostMatchEnd = 0;
	for (const confirmedToken of confirmedTokens) {
		if (
			ghostMatchEnd < ghostTokens.length &&
			normalize(confirmedToken) === normalize(ghostTokens[ghostMatchEnd])
		) {
			ghostMatchEnd++;
		}
	}

	// If ghost doesn't align with confirmed at all, it's likely a garbled
	// re-transcription from a restarted recognition session — discard it.
	if (ghostMatchEnd === 0) {
		return { text: confirmed, isFullyResolved: true };
	}

	const tailTokens = ghostTokens.slice(ghostMatchEnd);
	if (tailTokens.length > 0) {
		// Check if the tail is genuinely new content or just a re-transcription
		// of tokens confirmed already has (e.g. from a recognition restart).
		// If most tail tokens appear in confirmed, they're stale — discard.
		const confirmedSet = new Set(confirmedTokens.map(normalize));
		const staleCount = tailTokens.filter((w) => confirmedSet.has(normalize(w))).length;
		if (staleCount > tailTokens.length / 2) {
			return { text: confirmed, isFullyResolved: true };
		}

		const tail = joinWords(tailTokens);
		const separator = CJK_RANGE.test(tailTokens[0]) ? '' : ' ';
		return {
			text: confirmed + separator + tail,
			isFullyResolved: false,
		};
	}

	return { text: confirmed, isFullyResolved: true };
};
